import type { AssetRegistry } from "../assets/AssetRegistry";
import { compareSemver, loadBundleByRef } from "./OperatorBrandingService";
import { BOOT_REQUEST_TIMEOUT_MS } from "../boot/retry/BootRequestTimeout";
import { validateOperatorManifest, type OperatorManifest } from "./OperatorManifest";
import type { Log } from "@slot/presentation";

export interface EarlyBrandingResult {
  manifest: OperatorManifest;
  /**
   * URLs, not textures: the loader is DOM and paints before the renderer exists, so it hands these
   * straight to an <img> rather than waiting on a Pixi decode (decision 19).
   */
  loaderBackgroundUrl: string | null;
  loaderLogoUrl: string | null;
}

/**
 * Early (pre-loader) operator branding: one manifest
 * fetch (no retries; the boot races the whole call against a timeout), the version gate, then the
 * manifest's `loaderBundleUrl` (a bundle of the assets manifest, or a remote bundle descriptor) for
 * the loader's `background-intro` / `logo`. Every failure degrades to the default loader art. The
 * manifest is handed back so the full branding phase can skip its own fetch.
 */
export async function preloadOperatorLoaderBranding(input: {
  assets: AssetRegistry;
  operatorId: string;
  manifestBaseUrl: string;
  gameVersion?: string;
  log: Log;
}): Promise<EarlyBrandingResult | null> {
  const { assets, operatorId, manifestBaseUrl, gameVersion, log } = input;
  const base = manifestBaseUrl.replace(/\/+$/, "");
  const url = `${base}/operators/${encodeURIComponent(operatorId)}/manifest.json`;

  let rawPayload: unknown;
  try {
    const res = await fetch(url, { method: "GET", signal: AbortSignal.timeout(BOOT_REQUEST_TIMEOUT_MS) });
    if (!res.ok) {
      log(`[EarlyBranding] manifest fetch HTTP ${res.status} — skipping`);
      return null;
    }
    rawPayload = await res.json();
  } catch (err) {
    log(`[EarlyBranding] manifest fetch failed: ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }

  const validation = validateOperatorManifest(rawPayload, operatorId);
  if (validation.ok === false) {
    log(`[EarlyBranding] manifest invalid: ${validation.reason}`);
    return null;
  }
  const manifest = validation.manifest;

  if (manifest.minCompatibleGameVersion) {
    if (!gameVersion) {
      log(`[EarlyBranding] version gate: manifest requires ${manifest.minCompatibleGameVersion} but client version unknown`);
      return null;
    }
    if (compareSemver(gameVersion, manifest.minCompatibleGameVersion) < 0) {
      log(`[EarlyBranding] version gate: client ${gameVersion} < manifest ${manifest.minCompatibleGameVersion}`);
      return null;
    }
  }

  if (!manifest.loaderBundleUrl) {
    log(`[EarlyBranding] operator="${operatorId}" has no loaderBundleUrl — falling back to local default loader art`);
    return { manifest, loaderBackgroundUrl: null, loaderLogoUrl: null };
  }

  let bundle: string;
  try {
    bundle = await loadBundleByRef(assets, manifest.loaderBundleUrl, `operator-${operatorId}-loader`);
  } catch (err) {
    log(`[EarlyBranding] loader bundle load failed: ${err instanceof Error ? err.message : String(err)}`);
    return { manifest, loaderBackgroundUrl: null, loaderLogoUrl: null };
  }

  const loaderBackgroundUrl = resolveUrlSafe(assets, bundle, "background-intro", log);
  const loaderLogoUrl = resolveUrlSafe(assets, bundle, "logo", log);
  log(`[EarlyBranding] operator="${operatorId}" loader bundle loaded — bg=${!!loaderBackgroundUrl} logo=${!!loaderLogoUrl}`);
  return { manifest, loaderBackgroundUrl, loaderLogoUrl };
}

function resolveUrlSafe(assets: AssetRegistry, bundle: string, path: string, log: Log): string | null {
  const url = assets.resolveUrl(bundle, path);
  if (!url) log(`[EarlyBranding] asset "${path}" not found in operator bundle — using default`);
  return url;
}
